import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Hls from "hls.js";

const MEDIAMTX_HOST = process.env.REACT_APP_MEDIAMTX_HOST || "158.220.120.141";
// const MEDIAMTX_HOST = "localhost";
const PAYLOAD_HUB_BASE =
  process.env.PAYLOAD_HUB_BASE || "ws://localhost:44301/ai-camdevice-trackinghub";
const SIGNALR_RS = "\x1e";

/** Hub may send PascalCase (worker shape) or camelCase (.NET default JSON). */
function hubRouteCounter(o) {
  if (!o || typeof o !== "object") return null;
  return o.RouteCounter ?? o.routeCounter ?? null;
}
function hubRouteTraffic(o) {
  if (!o || typeof o !== "object") return null;
  return o.RouteTrafficAnalytics ?? o.routeTrafficAnalytics ?? null;
}
function hubRouteExposure(o) {
  if (!o || typeof o !== "object") return null;
  return o.RouteExposureCounter ?? o.routeExposureCounter ?? null;
}
function isFullHubTrackingPayload(o) {
  return (
    o &&
    typeof o === "object" &&
    !Array.isArray(o) &&
    hubRouteCounter(o) &&
    hubRouteExposure(o)
  );
}
/** SignalR client invocation: ReceiveTrackingMessage(deviceId, routeId, payload) per hub_receiver / receiver.py */
function extractTrackingPayloadFromHubMessage(msg) {
  const args = msg && Array.isArray(msg.arguments) ? msg.arguments : null;
  if (!args || args.length === 0) return null;
  if (
    args.length >= 3 &&
    args[2] &&
    typeof args[2] === "object" &&
    !Array.isArray(args[2]) &&
    (hubRouteCounter(args[2]) || hubRouteTraffic(args[2]))
  ) {
    return args[2];
  }
  for (const arg of args) {
    if (!arg || typeof arg !== "object" || Array.isArray(arg)) continue;
    if (isFullHubTrackingPayload(arg)) return arg;
    for (const inner of Object.values(arg)) {
      if (
        inner &&
        typeof inner === "object" &&
        !Array.isArray(inner) &&
        isFullHubTrackingPayload(inner)
      ) {
        return inner;
      }
    }
  }
  return null;
}

const OVERLAY_FLAG_DEFS = [
  {
    key: "showZoneCounterOverlay",
    label: "Daily exposure (all zones)",
    hint: "Scene-wide cumulative exposure P/V/Mb/B on the restream HUD.",
  },
  {
    key: "showEachZoneCounter",
    label: "Per-zone exposure",
    hint: "Z1_Exp style counters for each ROI (independent of all-zones row).",
  },
  {
    key: "showDwellTimeCounterOverlay",
    label: "Dwell totals (all zones)",
    hint: "Integrated dwell, completed dwell, and average visit durations (scene).",
  },
  {
    key: "showEachZoneDwelltimeC",
    label: "Per-zone dwell",
    hint: "Current visit average, CumAvgDT, intDwell, and done seconds per zone.",
  },
  {
    key: "showHumanPresentinZone",
    label: "Human present (instant)",
    hint: "Pedestrian counts and bbox labels for people inside ROIs.",
  },
  {
    key: "showVehiclePresentinZone",
    label: "Vehicle present (instant)",
    hint: "V/Mb/B counts, half-plane splits, and bbox labels for non-person classes.",
  },
  {
    key: "showTotalHumanCounter",
    label: "Total human counters",
    hint: "PedIO and line-crossing human in/out on the aggregate HUD block.",
  },
  {
    key: "showTotalVehicleCounter",
    label: "Total vehicle counters",
    hint: "VehIO and line-crossing vehicle in/out on the aggregate HUD block.",
  },
];

function pickDeviceBool(device, key) {
  if (!device || typeof device !== "object") return false;
  const v = device[key];
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === "string") return v.trim().toLowerCase() === "true";
  return !!v;
}

function overlayFlagsFromDevice(device) {
  const o = {};
  for (const { key } of OVERLAY_FLAG_DEFS) {
    o[key] = pickDeviceBool(device, key);
  }
  return o;
}

export default function DeviceDetail({
  device,
  token,
  streamMode = "webrtc",
  onDeviceSensorUpdated,
}) {
  const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const hubWsRef = useRef(null);
  const hubInvocationRef = useRef(1);
  const baselinePolygonsRef = useRef(new Set());
  const baselineLinesRef = useRef(new Set());
  const baselineSceneRef = useRef({ w: "", h: "", roi: false, line: false });
  const baselineOverlayRef = useRef(overlayFlagsFromDevice(null));

  /* ================= Scene & Stream ================= */
  const [scene, setScene] = useState(null);
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [isRoi, setIsRoi] = useState(false);
  const [isLine, setIsLine] = useState(false);

  const [roiStream, setRoiStream] = useState({ roiGatePointxywh: "" });
  const [lineStream, setLineStream] = useState({ lineCrossingSP: "", lineCrossingEP: "" });

  /* ================= Drawing State ================= */
  const [polygons, setPolygons] = useState([]);
  const [lines, setLines] = useState([]);
  const [currentPolygon, setCurrentPolygon] = useState(null);
  const [currentLine, setCurrentLine] = useState(null);

  const [addPolygonMode, setAddPolygonMode] = useState(false);
  const [editPolygonMode, setEditPolygonMode] = useState(false);
  const [deletePolygonMode, setDeletePolygonMode] = useState(false);

  const [addLineMode, setAddLineMode] = useState(false);
  const [editLineMode, setEditLineMode] = useState(false);
  const [deleteLineMode, setDeleteLineMode] = useState(false);

  const [selectedPolygonIndex, setSelectedPolygonIndex] = useState(null);
  const [selectedLineIndex, setSelectedLineIndex] = useState(null);
  const [draggingPoint, setDraggingPoint] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const [zoneCounts, setZoneCounts] = useState([]);
  const [lineCounts, setLineCounts] = useState([]);
  const [streamSize, setStreamSize] = useState({ width: 0, height: 0 });
  const [activeStreamMode, setActiveStreamMode] = useState(streamMode || "webrtc");
  const [editStatus, setEditStatus] = useState("");
const [showConfirm, setShowConfirm] = useState(false);
const [pendingAction, setPendingAction] = useState(null);
const [backupPolygons, setBackupPolygons] = useState([]);
const [backupLines, setBackupLines] = useState([]);

  const [overlayFlags, setOverlayFlags] = useState(() => overlayFlagsFromDevice(null));

  useEffect(() => {
    if (!device) return;
    const next = overlayFlagsFromDevice(device);
    setOverlayFlags(next);
    baselineOverlayRef.current = { ...next };
  }, [device]);


const handleConfirmYes = () => {
  setShowConfirm(false);
  setPendingAction(null);
};

const handleConfirmNo = () => {
  setShowConfirm(false);

  // revert changes
  if (pendingAction?.target === "polygon") {
    setPolygons(backupPolygons);
  }
  if (pendingAction?.target === "line") {
    setLines(backupLines);
  }

  setPendingAction(null);
};
  /* ================= Live Counts ================= */
  const [counts, setCounts] = useState({
    humansInZone: 0,
    vehiclesInZone: 0,
    humansIn: 0,
    humansOut: 0,
    vehiclesIn: 0,
    vehiclesOut: 0,
  });
  /** Latest full JSON body from the tracking hub (SendTrackingMessage), for dashboard debug. */
  const [hubPayloadJson, setHubPayloadJson] = useState("");
  /** Helps debug URL mismatch (worker PAYLOAD_HUB_BASE must match this). */
  const [hubWsStatus, setHubWsStatus] = useState("idle");
  useEffect(() => {
    setZoneCounts(
      polygons.map(() => ({
        humans: 0,
        vehicles: 0
      }))
    );
  }, [polygons]);

  useEffect(() => {
    setLineCounts(
      lines.map(() => ({
        humansIn: 0,
        humansOut: 0,
        vehiclesIn: 0,
        vehiclesOut: 0
      }))
    );
  }, [lines]);

  /* ================= Load Scene ================= */
  useEffect(() => {
    if (!device) return;

    axios.get("https://api.oohtech.ng/api/services/app/DeviceScene/GetAll", authConfig).then(res => {
      const sc = res.data.result.items.find(s => s.deviceSensorId === device.id);
      if (!sc) return;
      setScene(sc);
      setWidth(sc.sceneConfigWidth || "");
      setHeight(sc.sceneConfigHeight || "");
      setIsRoi(sc.isRoifilteringstream);
      setIsLine(sc.isLineCrossingStream);
      baselineSceneRef.current = {
        w: String(sc.sceneConfigWidth ?? ""),
        h: String(sc.sceneConfigHeight ?? ""),
        roi: !!sc.isRoifilteringstream,
        line: !!sc.isLineCrossingStream,
      };

      loadRoi(sc.id);
      loadLine(sc.id);
    });
  }, [device, token]);

  const loadRoi = (sceneId) => {
    axios.get("https://api.oohtech.ng/api/services/app/DeviceRoiFilteringStreamGate/GetAll", authConfig).then(res => {
      const roi = res.data.result.items.find(r => r.deviceSceneId === sceneId);
      if (!roi) return;
      setRoiStream({ ...roi, roiGatePointxywh: roi.roiGatePointxywh || "" });
      const parsed = roi.roiGatePointxywh ? parseMultiplePolygons(roi.roiGatePointxywh) : [];
      setPolygons(parsed);
      baselinePolygonsRef.current = new Set(parsed.map(polygonSignature));
    });
  };

  const loadLine = (sceneId) => {
    axios.get("https://api.oohtech.ng/api/services/app/DeviceLineCrossingStream/GetAll", authConfig).then(res => {
      const ln = res.data.result.items.find(l => l.deviceSceneId === sceneId);
      if (!ln) return;
      setLineStream({ ...ln, lineCrossingSP: ln.lineCrossingSP || "", lineCrossingEP: ln.lineCrossingEP || "" });
      const parsed = (ln.lineCrossingSP && ln.lineCrossingEP)
        ? parseMultipleLines(ln.lineCrossingSP, ln.lineCrossingEP)
        : [];
      setLines(parsed);
      baselineLinesRef.current = new Set(parsed.map(lineSignature));
    });
  };

  /* ================= Save ================= */
  const handleSave = async () => {
    if (!scene || !device) return;
    const jobs = [];

    // Update Scene
    jobs.push(axios.put("https://api.oohtech.ng/api/services/app/DeviceScene/Update", {
      ...scene,
      sceneConfigWidth: width,
      sceneConfigHeight: height,
      isRoifilteringstream: isRoi,
      isLineCrossingStream: isLine,
    }, authConfig));

    jobs.push(
      axios.put(
        "https://api.oohtech.ng/api/services/app/DeviceSensor/Update",
        {
          ...device,
          ...overlayFlags,
        },
        authConfig
      )
    );

    // Update ROI
    if (isRoi && roiStream) {
      const roiStr = polygons.map(p => p.points.flat().join(",")).join(";");
      const updatedRoi = { ...roiStream, roiGatePointxywh: roiStr, isEnable: true };
      setRoiStream(updatedRoi);
      jobs.push(axios.put("https://api.oohtech.ng/api/services/app/DeviceRoiFilteringStreamGate/Update", updatedRoi, authConfig));
    } else if (!isRoi && roiStream) {
      // Clear ROI when checkbox is off
      const clearedRoi = { ...roiStream, roiGatePointxywh: "", isEnable: false };
      setRoiStream(clearedRoi);
      jobs.push(axios.put("https://api.oohtech.ng/api/services/app/DeviceRoiFilteringStreamGate/Update", clearedRoi, authConfig));
    }

    // Update Lines
    if (isLine && lineStream) {
      const sp = lines.map(l => l.start.join(",")).join(";");
      const ep = lines.map(l => l.end.join(",")).join(";");
      const updatedLine = { ...lineStream, lineCrossingSP: sp, lineCrossingEP: ep, isEnable: true };
      setLineStream(updatedLine);
      jobs.push(axios.put("https://api.oohtech.ng/api/services/app/DeviceLineCrossingStream/Update", updatedLine, authConfig));
    } else if (!isLine && lineStream) {
      const clearedLine = { ...lineStream, lineCrossingSP: "", lineCrossingEP: "", isEnable: false };
      setLineStream(clearedLine);
      jobs.push(axios.put("https://api.oohtech.ng/api/services/app/DeviceLineCrossingStream/Update", clearedLine, authConfig));
    }

    try {
      await Promise.all(jobs);
      setAddPolygonMode(false);
      setDeletePolygonMode(false);
      setAddLineMode(false);
      setDeleteLineMode(false);
      setCurrentPolygon(null);
      setCurrentLine(null);
      setDraggingPoint(null);
      setEditStatus("");
      baselinePolygonsRef.current = new Set(polygons.map(polygonSignature));
      baselineLinesRef.current = new Set(lines.map(lineSignature));
      baselineSceneRef.current = {
        w: String(width),
        h: String(height),
        roi: !!isRoi,
        line: !!isLine,
      };
      baselineOverlayRef.current = { ...overlayFlags };
      onDeviceSensorUpdated?.({ ...device, ...overlayFlags });
      alert("Scene and device overlay flags saved. Python worker will auto-refresh from API.");
    } catch (err) {
      console.error("Save failed", err);
      alert("Save failed. Please retry.");
    }
  };

  const hubSubscribeInfo = useMemo(() => {
    if (!device) return { url: "", routeId: 0 };
    const routeId = Number(device.routeId || device.deviceRouteId || 0);
    return {
      url: `${PAYLOAD_HUB_BASE}?deviceId=${device.id}&routeId=${routeId}`,
      routeId,
    };
  }, [device]);

  /* ================= Payload Hub Live Data ================= */
  useEffect(() => {
    if (!device) return;
    setHubPayloadJson("");
    const { url: hubUrl, routeId } = hubSubscribeInfo;
    setHubWsStatus("connecting");
    const ws = new WebSocket(hubUrl);
    hubWsRef.current = ws;
    hubInvocationRef.current = 1;

    const sendInvoke = (target, args) => {
      const payload = {
        type: 1,
        invocationId: String(hubInvocationRef.current++),
        target,
        arguments: args,
      };
      ws.send(JSON.stringify(payload) + SIGNALR_RS);
    };

    const updateCountsFromHub = (trackingPayload) => {
      if (!trackingPayload) return;
      const routeCounter = hubRouteCounter(trackingPayload) || {};
      const traffic = hubRouteTraffic(trackingPayload) || {};
      setCounts((prev) => ({
        ...prev,
        humansInZone:
          routeCounter.HumanInZone ??
          routeCounter.humanInZone ??
          routeCounter.PedestrianInZone ??
          routeCounter.pedestrianInZone ??
          prev.humansInZone,
        vehiclesInZone:
          routeCounter.VehicleInZone ??
          routeCounter.vehicleInZone ??
          prev.vehiclesInZone,
        humansIn:
          traffic.HumanIn ?? traffic.humanIn ?? prev.humansIn,
        humansOut:
          traffic.HumanOut ?? traffic.humanOut ?? prev.humansOut,
        vehiclesIn:
          traffic.VehicleIn ?? traffic.vehicleIn ?? prev.vehiclesIn,
        vehiclesOut:
          traffic.VehicleOut ?? traffic.vehicleOut ?? prev.vehiclesOut,
      }));
    };

    ws.onopen = () => {
      setHubWsStatus("open");
      ws.send('{"protocol":"json","version":1}' + SIGNALR_RS);
      sendInvoke("RegisterDevice", [Number(device.id), routeId]);
      sendInvoke("ManageSubscription", [
        {
          Event: 1,
          Pair: [1, 2],
          DeviceId: Number(device.id),
          RouteId: routeId,
        },
      ]);
    };

    ws.onerror = () => {
      setHubWsStatus("error");
    };

    ws.onclose = () => {
      setHubWsStatus((s) => (s === "open" ? "closed" : s));
    };

    ws.onmessage = (evt) => {
      const chunks = String(evt.data || "")
        .split(SIGNALR_RS)
        .map((s) => s.trim())
        .filter(Boolean);
      chunks.forEach((chunk) => {
        if (chunk === "{}") return;
        let msg;
        try {
          msg = JSON.parse(chunk);
        } catch (error) {
          return;
        }
        if (msg.type === 6) return;
        const trackingPayload = extractTrackingPayloadFromHubMessage(msg);
        if (trackingPayload) {
          updateCountsFromHub(trackingPayload);
          if (isFullHubTrackingPayload(trackingPayload)) {
            setHubPayloadJson(JSON.stringify(trackingPayload, null, 2));
          }
        }
      });
    };

    return () => {
      hubWsRef.current = null;
      ws.close();
    };
  }, [device, hubSubscribeInfo]);

  useEffect(() => {
    setStreamSize({ width: 0, height: 0 });
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );
    }
  }, [device]);
  /* ================= Canvas Interaction ================= */
  const isCanvasEditActive =
    addPolygonMode ||
    deletePolygonMode ||
    addLineMode ||
    deleteLineMode ||
    Boolean(currentPolygon) ||
    Boolean(currentLine) ||
    Boolean(draggingPoint);
    const streamWidth = streamSize.width || ((videoRef.current && videoRef.current.videoWidth) || 0);
    const streamHeight = streamSize.height || ((videoRef.current && videoRef.current.videoHeight) || 0);
    const apiSceneWidth = Number(width) || (scene && Number(scene.sceneConfigWidth)) || 1280;
    const apiSceneHeight = Number(height) || (scene && Number(scene.sceneConfigHeight)) || 720;
  const inferredSourceBounds = useMemo(() => {
    let maxX = 0;
    let maxY = 0;
    polygons.forEach((poly) => {
      poly.points.forEach(([x, y]) => {
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      });
    });
    lines.forEach((line) => {
      const points = [line.start, line.end];
      points.forEach(([x, y]) => {
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      });
    });
    return { maxX, maxY };
  }, [polygons, lines]);
  const sourceCoordWidth = Math.max(apiSceneWidth, inferredSourceBounds.maxX + 1, 1);
  const sourceCoordHeight = Math.max(apiSceneHeight, inferredSourceBounds.maxY + 1, 1);
  const effectiveSceneWidth = streamWidth || apiSceneWidth;
  const effectiveSceneHeight = streamHeight || apiSceneHeight;
  const xRatio = effectiveSceneWidth / sourceCoordWidth;
  const yRatio = effectiveSceneHeight / sourceCoordHeight;

  const toScenePoint = (canvasX, canvasY) => [
    Math.round(canvasX / Math.max(1e-6, xRatio)),
    Math.round(canvasY / Math.max(1e-6, yRatio)),
  ];
  const toCanvasPoint = (scenePoint) => [
    Math.round(scenePoint[0] * xRatio),
    Math.round(scenePoint[1] * yRatio),
  ];

  const handleCanvasClick = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const canvasX = Math.round((e.clientX - rect.left) * scaleX);
    const canvasY = Math.round((e.clientY - rect.top) * scaleY);
    const [x, y] = toScenePoint(canvasX, canvasY);

    /* ---------------- Polygon ---------------- */
    if (addPolygonMode && isRoi) {
      if (!currentPolygon) setCurrentPolygon({ points: [[x, y]] });
      else {
        const first = currentPolygon.points[0];
        const [firstCanvasX, firstCanvasY] = toCanvasPoint(first);
        if (
          currentPolygon.points.length >= 3 &&
          Math.hypot(canvasX - firstCanvasX, canvasY - firstCanvasY) < 15
        ) {
          setPolygons([...polygons, { id: Date.now(), points: currentPolygon.points }]);
          setCurrentPolygon(null);
          setEditStatus("Polygon completed. Click Save to apply on API/video.");

                    // trigger confirm
          setAddPolygonMode(false);
          setPendingAction({ type: "add", target: "polygon" });
          setShowConfirm(true);
          return;
        }
        setCurrentPolygon({ points: [...currentPolygon.points, [x, y]] });
      }
    }

    if (editPolygonMode && isRoi) {
      const idx = polygons.findIndex(p => pointInPolygon([x, y], p.points));
      if (idx !== -1) setSelectedPolygonIndex(idx);
    }

    if (deletePolygonMode && isRoi) {
      const idx = polygons.findIndex((p) => {
        const canvasPoly = p.points.map(toCanvasPoint);
        return pointInPolygon([canvasX, canvasY], canvasPoly);
      });
      if (idx !== -1) {
        const newPolys = polygons.filter((_, i) => i !== idx);
        setPolygons(newPolys);

        setDeletePolygonMode(false);
        setPendingAction({ type: "delete", target: "polygon" });
        setShowConfirm(true);
        // setPolygons(polygons.filter((_, i) => i !== idx));
        // setEditStatus("Polygon deleted locally. Click Save to apply on API/video.");
      }
    }

    /* ---------------- Line ---------------- */
    if (addLineMode && isLine) {
      if (!currentLine) setCurrentLine({ start: [x, y], end: [x, y] });
      else {
        setLines([...lines, { id: Date.now(), start: currentLine.start, end: [x, y] }]);
        setCurrentLine(null);
        setEditStatus("Line completed. Click Save to apply on API/video.");

        setAddLineMode(false);
        setPendingAction({ type: "add", target: "line" });
        setShowConfirm(true);
      }
    }

    if (editLineMode && isLine) {
      const idx = lines.findIndex(l => pointNearLine([x, y], l.start, l.end));
      if (idx !== -1) setSelectedLineIndex(idx);
    }

    if (deleteLineMode && isLine) {
      const idx = lines.findIndex((l) => {
        const start = toCanvasPoint(l.start);
        const end = toCanvasPoint(l.end);
        return pointNearLine([canvasX, canvasY], start, end, 14);
      });
      if (idx !== -1) {
        // setLines(lines.filter((_, i) => i !== idx));
        // setEditStatus("Line deleted locally. Click Save to apply on API/video.");
        const newLines = lines.filter((_, i) => i !== idx);
        setLines(newLines);

        setDeleteLineMode(false);
        setPendingAction({ type: "delete", target: "line" });
        setShowConfirm(true);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!isCanvasEditActive) return;
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const canvasX = Math.round((e.clientX - rect.left) * scaleX);
    const canvasY = Math.round((e.clientY - rect.top) * scaleY);
    const [x, y] = toScenePoint(canvasX, canvasY);
    setMousePos([x, y]);

    // Dragging points
    if (draggingPoint) {
      const { type, polyIndex, pointIndex, lineIndex, end } = draggingPoint;
      if (type === "poly") {
        const newPolys = [...polygons];
        newPolys[polyIndex].points[pointIndex] = [x, y];
        setPolygons(newPolys);
      } else if (type === "line") {
        const newLines = [...lines];
        newLines[lineIndex][end] = [x, y];
        setLines(newLines);
      }
    }
  };

  const handleContextMenu = (e) => {
    if (!isCanvasEditActive) return;
    e.preventDefault();
    if (currentPolygon) {
      const pts = currentPolygon.points.slice(0, -1);
      setCurrentPolygon(pts.length > 0 ? { points: pts } : null);
    }
    if (currentLine) setCurrentLine(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace") {
      if (draggingPoint) {
        const { type, polyIndex, pointIndex, lineIndex } = draggingPoint;
        if (type === "poly") {
          const newPolys = [...polygons];
          newPolys[polyIndex].points.splice(pointIndex, 1);
          setPolygons(newPolys);
          setDraggingPoint(null);
        } else if (type === "line") {
          const newLines = [...lines];
          newLines.splice(lineIndex, 1);
          setLines(newLines);
          setDraggingPoint(null);
        }
      }
    }
  };

  /* ================= Draw Canvas ================= */
  useEffect(() => {
    if (!isCanvasEditActive) return;
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const canvas = canvasRef.current;
    const renderWidth = effectiveSceneWidth;
    const renderHeight = effectiveSceneHeight;

    if (canvas.width !== renderWidth || canvas.height !== renderHeight) {
      canvas.width = renderWidth;
      canvas.height = renderHeight;
    }
    ctx.clearRect(0, 0, renderWidth, renderHeight);

      // Keep stream visuals from Python; draw only transient edit preview.
      const pendingPolygons = polygons.filter(
        (p) => !baselinePolygonsRef.current.has(polygonSignature(p))
      );
      const pendingLines = lines.filter(
        (l) => !baselineLinesRef.current.has(lineSignature(l))
      );

      pendingPolygons.forEach((p, i) => {
        const canvasPoints = p.points.map(toCanvasPoint);
        drawPolygon(ctx, canvasPoints);
        const [x, y] = canvasPoints[0];
        ctx.fillStyle = "#00e5ff";
        ctx.font = "bold 13px Arial";
        ctx.fillText(`Pending Zone ${i + 1}`, x + 6, y - 6);
      });

      pendingLines.forEach((l, i) => {
        const start = toCanvasPoint(l.start);
        const end = toCanvasPoint(l.end);
        drawLine(ctx, start, end);
        const mx = (start[0] + end[0]) / 2;
        const my = (start[1] + end[1]) / 2;
        ctx.fillStyle = "#00e5ff";
        ctx.font = "bold 13px Arial";
        ctx.fillText(`Pending Line ${i + 1}`, mx + 6, my - 6);
      });

      if (addPolygonMode && currentPolygon && mousePos) {
        const pts = [...currentPolygon.points, mousePos];
        drawPolygon(ctx, pts.map(toCanvasPoint), false);
        currentPolygon.points.forEach((p) => drawCircle(ctx, toCanvasPoint(p)));
      }

      if (addLineMode && currentLine && mousePos) {
        drawLine(ctx, toCanvasPoint(currentLine.start), toCanvasPoint(mousePos), true);
      }

  }, [
    polygons,
    currentPolygon,
    lines,
    currentLine,
    mousePos,
    width,
    height,
    scene,
    isCanvasEditActive,
    effectiveSceneWidth,
    effectiveSceneHeight,
    sourceCoordWidth,
    sourceCoordHeight,
    activeStreamMode,
  ]);

  /* ================= UI ================= */
  const streamPath = `device${device?.id}`;
  const streamUrls = {
    webrtc: `http://${MEDIAMTX_HOST}:8889/${streamPath}/`,
    hls: `http://${MEDIAMTX_HOST}:8888/${streamPath}/index.m3u8`,
    llhls: `http://${MEDIAMTX_HOST}:8888/${streamPath}/index.m3u8?lowLatency=1`,
    rtsp: `rtsp://${MEDIAMTX_HOST}:8554/${streamPath}`,
  };
  const selectedUrl = streamUrls[activeStreamMode] || streamUrls.webrtc;

  useEffect(() => {
    setActiveStreamMode(streamMode || "webrtc");
  }, [streamMode, device?.id]);

  useEffect(() => {
    if (!videoRef.current) return undefined;
    if (activeStreamMode !== "hls" && activeStreamMode !== "llhls") return undefined;

    const video = videoRef.current;
    let hls;

    if (Hls.isSupported()) {
      hls = new Hls({
        lowLatencyMode: activeStreamMode === "llhls",
        backBufferLength: 30,
      });
      hls.loadSource(selectedUrl);
      hls.attachMedia(video);
    } else {
      video.src = selectedUrl;
    }

    return () => {
      if (hls) hls.destroy();
    };
  }, [selectedUrl, activeStreamMode]);

  const hasUnsavedChanges = (() => {
    if (!scene) return false;
    const bs = baselineSceneRef.current;
    const curP = new Set(polygons.map(polygonSignature));
    const baseP = baselinePolygonsRef.current;
    if (curP.size !== baseP.size) return true;
    for (const s of curP) {
      if (!baseP.has(s)) return true;
    }
    const curL = new Set(lines.map(lineSignature));
    const baseL = baselineLinesRef.current;
    if (curL.size !== baseL.size) return true;
    for (const s of curL) {
      if (!baseL.has(s)) return true;
    }
    if (String(width) !== String(bs.w) || String(height) !== String(bs.h)) return true;
    if (!!isRoi !== !!bs.roi || !!isLine !== !!bs.line) return true;
    for (const { key } of OVERLAY_FLAG_DEFS) {
      if (!!overlayFlags[key] !== !!baselineOverlayRef.current[key]) return true;
    }
    return false;
  })();

  return (
  <div
    tabIndex={0}
    onKeyDown={handleKeyDown}
    className="device-panel"
  >
    <div className="device-detail-layout">
      
      {/* ================= LEFT CONTROL PANEL ================= */}
      <div className="device-controls">
        <h3 className="device-title">
          Device {device?.name || device?.id}
        </h3>
        <div className="size-row">
          <label>
            Width
            <input value={width} onChange={e => setWidth(e.target.value)} />
          </label>
          <label>
            Height
            <input value={height} onChange={e => setHeight(e.target.value)} />
          </label>
        </div>

        <fieldset className="stream-overlay-fieldset">
          <legend>Restream overlay (DeviceSensor)</legend>
          <p className="overlay-fieldset-intro">
            These booleans are stored on the device row. The Python worker reloads them whenever it polls the
            API for scene geometry (interval <code>API_POLL_SECONDS</code> in <code>DeviceSensor/worker.py</code>) and
            toggles on-video analytics accordingly.
          </p>
          <div className="overlay-flags-grid">
            {OVERLAY_FLAG_DEFS.map(({ key, label, hint }) => (
              <label key={key} className="overlay-flag-item" title={hint}>
                <input
                  type="checkbox"
                  checked={!!overlayFlags[key]}
                  onChange={() =>
                    setOverlayFlags((prev) => ({ ...prev, [key]: !prev[key] }))
                  }
                />
                <span className="overlay-flag-ui" aria-hidden="true" />
                <span className="overlay-flag-text">
                  <span className="overlay-flag-label">{label}</span>
                  <span className="overlay-flag-hint">{hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="toggle-row">
          ROI
          <input
            type="checkbox"
            checked={isRoi}
            onChange={() => {
              const newVal = !isRoi;
              setIsRoi(newVal);
              if (!newVal) {
                setAddPolygonMode(false);
                setEditPolygonMode(false);
                setDeletePolygonMode(false);
                setPolygons([]);
                setRoiStream({ ...roiStream, roiGatePointxywh: "" });
              }
            }}
          />
        </label>

        <div className={`config-row ${!isRoi ? "hidden" : ""}`}>
          <input
            value={roiStream.roiGatePointxywh}
            onChange={e =>
              setRoiStream({ ...roiStream, roiGatePointxywh: e.target.value })
            }
          />
          {/* <button onClick={() => {
            setAddPolygonMode(true);
            setEditPolygonMode(false);
            setDeletePolygonMode(false);
            setAddLineMode(false);
            setEditLineMode(false);
            setDeleteLineMode(false);
          }}>Add Polygon</button> */}

          {/* <button onClick={() => {
            setAddPolygonMode(false);
            setEditPolygonMode(true);
            setDeletePolygonMode(false);
          }}>Edit Polygon</button> */}

          {/* <button className="danger" onClick={() => {
            setAddPolygonMode(false);
            setEditPolygonMode(false);
            setDeletePolygonMode(true);
            setAddLineMode(false);
            setEditLineMode(false);
            setDeleteLineMode(false);
          }}>Delete Polygon</button> */}

          <button
            className={addPolygonMode ? "active-btn" : ""}
            onClick={() => {
              setBackupPolygons(polygons);
              setAddPolygonMode(true);
              setDeletePolygonMode(false);
              setAddLineMode(false);
              setDeleteLineMode(false);
            }}
          >
            Add Polygon
          </button>

          <button
            className={`danger ${deletePolygonMode ? "active-btn" : ""}`}
            onClick={() => {
              setBackupPolygons(polygons);
              setDeletePolygonMode(true);
              setAddPolygonMode(false);
              setAddLineMode(false);
              setDeleteLineMode(false);
            }}
          >
            Delete Polygon
          </button>
        </div>

        <label className="toggle-row">
          Line
          <input
            type="checkbox"
            checked={isLine}
            onChange={() => {
              const newVal = !isLine;
              setIsLine(newVal);
              if (!newVal) {
                setAddLineMode(false);
                setEditLineMode(false);
                setDeleteLineMode(false);
                setLines([]);
                setLineStream({
                  ...lineStream,
                  lineCrossingSP: "",
                  lineCrossingEP: ""
                });
              }
            }}
          />
        </label>

        <div className={`config-row ${!isLine ? "hidden" : ""}`}>
          <input
            value={lineStream.lineCrossingSP}
            onChange={e =>
              setLineStream({ ...lineStream, lineCrossingSP: e.target.value })
            }
          />
          <input
            value={lineStream.lineCrossingEP}
            onChange={e =>
              setLineStream({ ...lineStream, lineCrossingEP: e.target.value })
            }
          />

          {/* <button onClick={() => {
            setAddLineMode(true);
            setEditLineMode(false);
            setDeleteLineMode(false);
            setAddPolygonMode(false);
            setEditPolygonMode(false);
            setDeletePolygonMode(false);
          }}>Add Line</button> */}

          {/* <button onClick={() => {
            setAddLineMode(false);
            setEditLineMode(true);
            setDeleteLineMode(false);
          }}>Edit Line</button> */}

          {/* <button className="danger" onClick={() => {
            setAddLineMode(false);
            setEditLineMode(false);
            setDeleteLineMode(true);
            setAddPolygonMode(false);
            setEditPolygonMode(false);
            setDeletePolygonMode(false);
          }}>Delete Line</button> */}
          <button
            className={addLineMode ? "active-btn" : ""}
            onClick={() => {
              setBackupLines(lines);
              setAddLineMode(true);
              setDeleteLineMode(false);
              setAddPolygonMode(false);
              setDeletePolygonMode(false);
            }}
          >
            Add Line
          </button>

          <button
            className={`danger ${deleteLineMode ? "active-btn" : ""}`}
            onClick={() => {
              setBackupLines(lines);
              setDeleteLineMode(true);
              setAddLineMode(false);
              setAddPolygonMode(false);
              setDeletePolygonMode(false);
            }}
          >
            Delete Line
          </button>
        </div>

        <button
          type="button"
          className="save-button"
          disabled={!scene || !hasUnsavedChanges}
          title={!scene ? "Load a device scene first" : !hasUnsavedChanges ? "No changes to save" : "Save scene, ROI, and lines to the API"}
          onClick={handleSave}
        >
          Save to API
        </button>
        {editStatus && <p className="helper-text">{editStatus}</p>}
        <div className="counts-view">
          <h4>Stream Output ({activeStreamMode.toUpperCase()})</h4>
          <a href={selectedUrl} target="_blank" rel="noreferrer">
            {selectedUrl}
          </a>
          {(activeStreamMode === "hls" || activeStreamMode === "llhls") && (
            <video
              className="stream-preview"
              controls
              muted
              autoPlay
              src={selectedUrl}
            />
          )}
          {activeStreamMode === "webrtc" && (
            <p className="helper-text">
              WebRTC is selected for low-latency playback.
            </p>
          )}
        </div>

        <div className="hub-payload-panel">
          <h4>Tracking hub payload (latest)</h4>
          <p className="helper-text hub-payload-hint">
            Same WebSocket the worker uses (<code>PAYLOAD_HUB_BASE</code> / <code>REACT_APP_PAYLOAD_HUB_BASE</code>).
            The hub usually calls clients with <code>ReceiveTrackingMessage</code> and arguments{" "}
            <code>[deviceId, routeId, payload]</code>.
          </p>
          <p className="helper-text hub-payload-meta">
            <strong>URL:</strong>{" "}
            <span className="hub-payload-url">{hubSubscribeInfo.url || "—"}</span>
            <br />
            <strong>WebSocket:</strong> {hubWsStatus}
            {hubSubscribeInfo.routeId <= 0 && (
              <>
                <br />
                <span className="hub-payload-warn">
                  routeId is 0 — the Python worker will not call <code>SendTrackingMessage</code> for this device.
                </span>
              </>
            )}
          </p>
          <div className="hub-payload-scroll">
            {hubPayloadJson ? (
              <pre className="hub-payload-pre">{hubPayloadJson}</pre>
            ) : (
              <p className="helper-text">
                No payload yet. If the socket is <code>open</code> but this stays empty, the hub may not be
                broadcasting, or property names may differ (check browser devtools → Network → WS frames).
              </p>
            )}
          </div>
        </div>
        {/* <div className="counts-view">
        <ul>
            <li>Humans in zone: {counts.humansInZone}</li>
            <li>Vehicles in zone: {counts.vehiclesInZone}</li>
            <li>Humans entered: {counts.humansIn}</li>
            <li>Humans exited: {counts.humansOut}</li>
            <li>Vehicles entered: {counts.vehiclesIn}</li>
            <li>Vehicles exited: {counts.vehiclesOut}</li>
        </ul>
        </div> */}
        {/* <div className="counts-view">
          <h4>Total</h4>
          <ul>
            <li>Humans in zone: {counts.humansInZone}</li>
            <li>Vehicles in zone: {counts.vehiclesInZone}</li>
            <li>Humans entered: {counts.humansIn}</li>
            <li>Humans exited: {counts.humansOut}</li>
            <li>Vehicles entered: {counts.vehiclesIn}</li>
            <li>Vehicles exited: {counts.vehiclesOut}</li>
          </ul>

          {zoneCounts.map((z, i) => (
            <div key={i}>
              <strong>Zone {i + 1}</strong>
              <ul>
                <li>Humans in zone: {z.humans}</li>
                <li>Vehicles in zone: {z.vehicles}</li>
              </ul>
            </div>
          ))}

          {lineCounts.map((l, i) => (
            <div key={i}>
              <strong>Line {i + 1}</strong>
              <ul>
                <li>Humans entered: {l.humansIn}</li>
                <li>Humans exited: {l.humansOut}</li>
                <li>Vehicles entered: {l.vehiclesIn}</li>
                <li>Vehicles exited: {l.vehiclesOut}</li>
              </ul>
            </div>
          ))}

        </div> */}

        {/* <pre className="counts-view">
          {JSON.stringify(counts, null, 2)}
        </pre> */}
      </div>

      {/* ================= RIGHT VIDEO + OVERLAY CANVAS ================= */}
      <div
        className="device-canvas-wrapper video-overlay-container"
        style={{ "--stream-aspect": `${effectiveSceneWidth} / ${effectiveSceneHeight}` }}
      >
        {(activeStreamMode === "hls" || activeStreamMode === "llhls") && (
          <video
            ref={videoRef}
            className="live-video-feed"
            autoPlay
            muted
            controls
            playsInline
            onLoadedMetadata={(e) => {
              const vw = e.currentTarget.videoWidth || 0;
              const vh = e.currentTarget.videoHeight || 0;
              setStreamSize({ width: vw, height: vh });
            }}
          />
        )}
        {activeStreamMode === "webrtc" && (
          <iframe
            className="live-video-feed webrtc-frame"
            src={selectedUrl}
            title={`MediaMTX WebRTC ${device?.id}`}
            allow="autoplay; fullscreen; picture-in-picture"
          />
        )}
        {activeStreamMode === "rtsp" && (
          <div className="rtsp-placeholder">
            <p>RTSP cannot play directly in browser.</p>
            <a href={selectedUrl} target="_blank" rel="noreferrer">{selectedUrl}</a>
          </div>
        )}
        {isCanvasEditActive && (
          <canvas
            className="overlay-canvas"
            ref={canvasRef}
            onClick={handleCanvasClick}
            onMouseMove={handleMouseMove}
            onContextMenu={handleContextMenu}
          />
        )}
      </div>
    </div>
    {showConfirm && (
      <div className="confirm-modal">
        <div className="confirm-box">
          <p>
            {pendingAction?.type === "add"
              ? `Keep this new ${pendingAction.target === "polygon" ? "zone" : "line"}? Click “Save to API” when you are ready to update the worker.`
              : `Keep this deletion? Click “Save to API” when you are ready to update the worker.`}
          </p>

          <div className="confirm-actions">
            <button type="button" onClick={handleConfirmYes}>Keep</button>
            <button type="button" onClick={handleConfirmNo}>Undo</button>
          </div>
        </div>
      </div>
    )}
  </div>
);
}

/* ================= Utilities ================= */
function parseMultiplePolygons(str) {
  if (!str) return [];
  return str.split(";").filter(s => s).map(poly => {
    const nums = poly.split(",").map(Number);
    const points = [];
    for (let i = 0; i < nums.length; i += 2) points.push([nums[i], nums[i + 1]]);
    return { id: Date.now() + Math.random(), points };
  });
}

function parseMultipleLines(spStr, epStr) {
  const sArr = spStr.split(";").filter(s => s).map(s => s.split(",").map(Number));
  const eArr = epStr.split(";").filter(s => s).map(s => s.split(",").map(Number));
  const res = [];
  for (let i = 0; i < sArr.length; i++) res.push({ id: Date.now() + i, start: sArr[i], end: eArr[i] });
  return res;
}

function drawPolygon(ctx, points, fill = true) {
  if (!points.length) return;
  ctx.beginPath();
  points.forEach((p, i) => i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1]));
  ctx.closePath();
  if (fill) { ctx.fillStyle = "rgba(255,255,0,0.3)"; ctx.fill(); }
  ctx.strokeStyle = "gold"; ctx.lineWidth = 2; ctx.stroke();
}

function drawLine(ctx, start, end, temp = false) {
  ctx.beginPath(); ctx.moveTo(start[0], start[1]); ctx.lineTo(end[0], end[1]);
  ctx.strokeStyle = temp ? "orange" : "red"; ctx.lineWidth = 2; ctx.stroke();
}

function drawCircle(ctx, p) {
  ctx.beginPath(); ctx.arc(p[0], p[1], 5, 0, Math.PI * 2); ctx.fillStyle = "yellow"; ctx.fill(); ctx.strokeStyle = "orange"; ctx.stroke();
}

function pointInPolygon(point, vs) {
  let [x, y] = point; let inside = false;
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const [xi, yi] = vs[i]; const [xj, yj] = vs[j];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointNearLine(p, start, end, tolerance = 14) {
  const dx = end[0] - start[0]; const dy = end[1] - start[1];
  const len2 = dx*dx + dy*dy;
  const t = ((p[0]-start[0])*dx + (p[1]-start[1])*dy)/len2;
  const closest = [start[0]+t*dx, start[1]+t*dy];
  const dist = Math.hypot(p[0]-closest[0], p[1]-closest[1]);
  return dist < tolerance;
}

function polygonSignature(polygon) {
  return polygon.points.map((p) => `${p[0]}:${p[1]}`).join("|");
}

function lineSignature(line) {
  return `${line.start[0]}:${line.start[1]}-${line.end[0]}:${line.end[1]}`;
}